/**
 * @file Admin /secrets panel route contracts.
 * @module @labcraft/admin/shared/secrets-routes
 *
 * The admin daemon owns the operator-facing surface for rotating
 * every per-daemon secret in OpenBao. Two routes:
 *
 *   - `GET /api/secrets` — per-secret declarations + rotation
 *     metadata (last-rotated timestamp + version). The values
 *     themselves NEVER cross the wire here; the UI is write-only.
 *   - `POST /api/secrets/rotate` — operator pastes the new value.
 *     The admin daemon writes it to Bao via its own AppRole
 *     (`infra/openbao/policies.tf` § `vault_policy.admin`).
 *
 * Both routes are gated on the admin session cookie
 * (`lc_admin_session`); 401 for anonymous, 200/204 for
 * authenticated operators. The audit log entry on Bao records the
 * server-side AppRole's identity — the named-operator identity is
 * recorded via `X-Vault-Audit-Header` carrying the admin's UUID.
 */

import { z } from 'zod'
import { defineRoute } from '@labcraft/core'
import {
  RotateSecretRequestSchema,
  SecretDefinitionSchema,
} from '@labcraft/openbao/shared'

////////////////////////////////////////////////////////////////////////////////
// GET /api/secrets
////////////////////////////////////////////////////////////////////////////////

/**
 * One row in the list response. Carries the declaration plus the
 * latest rotation metadata — never the value itself.
 */
const SecretListEntrySchema = z.object({
  definition: SecretDefinitionSchema,
  lastRotated: z.string().datetime().nullable(),
  version: z.number().int().positive().nullable(),
})

/**
 * Response of `GET /api/secrets`. One entry per declared secret;
 * grouped by daemon at render time (the UI sorts by
 * `definition.daemon`).
 */
export const SecretsListResponseSchema = z.object({
  entries: z.array(SecretListEntrySchema),
})

/** Parsed `SecretsListResponse`. */
export type SecretsListResponse = z.infer<typeof SecretsListResponseSchema>

/** `GET /api/secrets` — per-secret metadata, never values. */
export const SecretsListRoute = defineRoute({
  method: 'GET',
  path: '/api/secrets',
  response: SecretsListResponseSchema,
  description:
    'List declared secrets + rotation metadata. Values never returned.',
})

////////////////////////////////////////////////////////////////////////////////
// POST /api/secrets/rotate
////////////////////////////////////////////////////////////////////////////////

/** Request body — mirrors `RotateSecretRequest` from shared/. */
export { RotateSecretRequestSchema }

/**
 * Response of `POST /api/secrets/rotate`. Returns the new version
 * + timestamp so the UI can update the panel row without a follow-
 * up GET.
 */
export const RotateSecretResponseSchema = z.object({
  path: z.string().min(1),
  version: z.number().int().positive(),
  rotatedAt: z.string().datetime(),
})

/** Parsed `RotateSecretResponse`. */
export type RotateSecretResponse = z.infer<typeof RotateSecretResponseSchema>

/** `POST /api/secrets/rotate` — write a new value for one secret path. */
export const RotateSecretRoute = defineRoute({
  method: 'POST',
  path: '/api/secrets/rotate',
  request: RotateSecretRequestSchema,
  response: RotateSecretResponseSchema,
  description: 'Rotate one secret value (writes to Bao kv/<path>).',
})
